<template>
  <div class="min-h-screen bg-black text-white pixel-font flex flex-col mx-auto">
    <AppHeader :activeTab="activeTab" @tab-changed="handleTabChange" />
    <div class="hero-logo-wrap">
      <a href="/" class="hero-logo-link">
        <img src="@/assets/logos/mc-icons-logo.png" alt="MCIcons" class="hero-logo" />
      </a>
    </div>
    <div class="powered-by">
      <div class="flex flex-nowrap whitespace-nowrap items-center gap-2 text-sm text-gray-400">
        <span>Powered by</span>
        <a href="https://ccleaf.com" target="_blank" rel="noopener"
          class="maintainer flex items-center gap-1 hover:text-white">
          <img src="@/assets/logos/ccleaf_logo.png" alt="ccLeaf" class="h-5 w-auto" />
          <span>ccLeaf</span>
        </a>
      </div>
    </div>
    <main class="container mx-auto px-4 pt-1 max-w-6xl flex flex-col md:flex-row gap-3 flex-grow">
      <MobileMenuToggle
        v-if="activeTab === 'icons'"
        :is-open="isMobileMenuOpen"
        class="block md:hidden"
        @toggle="toggleMobileMenu"
      />
      <AppSidebar
        v-if="activeTab === 'icons'"
        :isMobileMenuOpen="isMobileMenuOpen"
        :isSmallScreen="isSmallScreen"
        :searchQuery="searchQuery"
        @category-selected="handleCategorySelected"
        @update:searchQuery="searchQuery = $event"
      />
      
      <!-- Tab Content -->
      <div v-if="activeTab === 'icons'" class="flex-grow">
        <MainGrid
          :selectedCategory="selectedCategory"
          :searchQuery="searchQuery"
          :resetTrigger="resetTrigger"
          @update:searchQuery="searchQuery = $event"
        />
      </div>
      
      <!-- Textures Tab -->
      <div v-else-if="activeTab === 'textures'" class="w-full flex-grow">
        <TexturesGenerator />
      </div>

      <!-- Skins Tab -->
      <div v-else-if="activeTab === 'skins'" class="w-full flex-grow">
        <SkinsGenerator />
      </div>
    </main>
    <DiscordBanner />
    <AppFooter />
    <GoToTopButton
      :visible="showGoToTop"
      @click="scrollToTop"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { authenticatedGet } from './utils/auth';

import AppHeader from './components/AppHeader.vue';
import MobileMenuToggle from './components/MobileMenuToggle.vue';
import AppSidebar from './components/AppSidebar.vue';
import MainGrid from './components/MainGrid.vue';
import TexturesGenerator from './components/TexturesGenerator.vue';
import SkinsGenerator from './components/SkinsGenerator.vue';
import DiscordBanner from './components/DiscordBanner.vue';
import AppFooter from './components/AppFooter.vue';
import GoToTopButton from './components/GoToTopButton.vue';

const PRODUCT_NAME = 'Vault - ccLeaf';
const DEFAULT_TITLE = `Icons | ${PRODUCT_NAME}`;
const titles = {
  'icons': `Icons | ${PRODUCT_NAME}`,
  'textures': `Textures | ${PRODUCT_NAME}`,
  'skins': `Skins | ${PRODUCT_NAME}`,
};

const setPageTitle = (title) => {
  document.title = title;
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', title);
  const metaTitle = document.querySelector('meta[name="title"]');
  if (metaTitle) metaTitle.setAttribute('content', title);
};

const searchQuery = ref('');
const showGoToTop = ref(false);
const isMobileMenuOpen = ref(false);
const isSmallScreen = ref(window.innerWidth < 768);
const categories = ref([]);
const activeTab = ref('icons');

// Add a new ref for reset trigger
const resetTrigger = ref(0);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const checkScroll = () => {
  showGoToTop.value = window.pageYOffset > 300;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleResize = () => {
  isSmallScreen.value = window.innerWidth < 768;
  if (!isSmallScreen.value) {
    isMobileMenuOpen.value = true;
  }
};

const handleTabChange = (tabId) => {
  const previousTab = activeTab.value;
  activeTab.value = tabId;
  
  // Update URL based on tab
  const paths = {
    'icons': '/',
    'textures': '/textures',
    'skins': '/skins'
  };
  
  // Update document title based on selected tab
  const title = titles[tabId] || DEFAULT_TITLE;
  setPageTitle(title);

  // Use history API to update the URL without reloading
  const newPath = paths[tabId] || '/';
  if (window.location.pathname !== newPath) {
    window.history.pushState({}, '', newPath);
    
    // If we're coming back to the icons tab from another tab, reset to "All" category
    if (tabId === 'icons' && previousTab !== 'icons') {
      selectedCategory.value = 'All';
      resetTrigger.value++;
    }
  }
};

const fetchCategories = async () => {
  try {
    const response = await authenticatedGet('/api/categories');
    categories.value = response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
  }
};

const selectedCategory = ref('All');

const handleCategorySelected = (category) => {
  selectedCategory.value = category;
  // Don't clear searchQuery here to retain the search when switching categories
};

// Create a function to update tab based on path
const updateTabFromPath = () => {
  const path = window.location.pathname;
  console.log("Current path:", path);
  
  if (path.includes('/textures')) {
    activeTab.value = 'textures';
  } else if (path.includes('/skins')) {
    activeTab.value = 'skins';
  } else {
    activeTab.value = 'icons';
  }
  setPageTitle(titles[activeTab.value] || DEFAULT_TITLE);
  
  console.log("Active tab set to:", activeTab.value);
};

onMounted(() => {
  window.addEventListener('scroll', checkScroll);
  window.addEventListener('resize', handleResize);
  window.addEventListener('popstate', () => {
    // Handle browser back/forward button
    updateTabFromPath();
  });
  
  // Check for tab redirection from localStorage
  const tabsRedirect = localStorage.getItem('tabsRedirect');
  if (tabsRedirect) {
    // Set active tab based on localStorage value
    activeTab.value = tabsRedirect;
    setPageTitle(titles[tabsRedirect] || DEFAULT_TITLE);

    // Clear the localStorage
    localStorage.removeItem('tabsRedirect');
    // Update URL without page reload
    const paths = {
      'icons': '/',
      'textures': '/textures',
      'skins': '/skins'
    };
    const newPath = paths[tabsRedirect] || '/';
    window.history.pushState({}, '', newPath);
  } else {
    // Set initial tab based on URL if no localStorage redirection
    updateTabFromPath();
  }
  
  handleResize();
  fetchCategories();
});

onUnmounted(() => {
  window.removeEventListener('scroll', checkScroll);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('popstate', () => {});
});
</script>

<style scoped>
.hero-logo-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
  padding: 32px 16px 48px;
}

.hero-logo-link {
  display: inline-flex;
  margin: 0 auto;
  transition: transform 0.2s ease;
}

.hero-logo-link:hover {
  transform: scale(1.03);
}

.hero-logo {
  width: 25.3125rem;
  max-width: 90%;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.powered-by {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: -2rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #9ca3af;
}

.maintainer {
  transition: color 0.2s ease, transform 0.2s ease;
}

.maintainer img {
  transition: transform 0.25s ease, filter 0.25s ease;
}

.maintainer:hover,
.maintainer:focus-visible {
  transform: translateY(-2px);
}

.maintainer:hover img,
.maintainer:focus-visible img {
  transform: scale(1.15) rotate(-4deg);
  filter: drop-shadow(0 2px 6px rgba(255, 255, 255, 0.35));
}

@media (prefers-reduced-motion: reduce) {
  .maintainer,
  .maintainer img {
    transition: none;
  }

  .maintainer:hover,
  .maintainer:focus-visible,
  .maintainer:hover img,
  .maintainer:focus-visible img {
    transform: none;
  }
}

@media (max-width: 640px) {
  .hero-logo-wrap { padding: 20px 12px 24px; }
  .hero-logo { width: 16.875rem; }
  .powered-by { margin-top: -0.5rem; }
}
</style>
